import { EditOutlined } from '@ant-design/icons';
import { ConnectError } from '@bufbuild/connect';
import { Button, notification, Typography } from 'antd';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { Organization } from 'protocol/model/dto_organization_pb';

import { PATHS } from '@/shared/config';
import { useAppDispatch } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';
import {
  DSOS_FILTER,
  OrganizationSearchSelect,
  organizationsMessages,
} from '@/entities/organizations';
import { userModel } from '@/entities/user';

import { parentDSOMessages } from '../../config';

import styles from './OrganizationParentDSO.module.scss';

const { Text } = Typography;

type OrganizationParentDSOProps = {
  canManageParent: boolean;
  isEditorVisible: boolean;
  organizationID: string;
  parentID: string;
  parentEmail?: string;
  parentName?: string;
  // The parent itself, when the page already holds it. Lets the picker render a
  // label instead of the bare id it was given as a value.
  parentOrganization?: Organization;
  onEditorVisibleChange: (visible: boolean) => void;
};

export const OrganizationParentDSO: FC<OrganizationParentDSOProps> = (
  props,
) => {
  const {
    canManageParent,
    isEditorVisible,
    organizationID,
    parentID,
    parentEmail,
    parentName,
    parentOrganization,
    onEditorVisibleChange,
  } = props;

  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [newParentID, setNewParentID] = useState<string | undefined>(parentID);
  const [loading, setLoading] = useState<boolean>(false);

  // The stream can change the parent while this component stays mounted, so the
  // draft is taken from the current value each time the editor opens.
  const showEditor = () => {
    setNewParentID(parentID);
    onEditorVisibleChange(true);
  };

  const handleCancel = () => {
    onEditorVisibleChange(false);
    setNewParentID(parentID);
  };

  const handleUpdateParent = async () => {
    setLoading(true);

    try {
      const { Organization: updatedOrganization } = await dispatch(
        userModel.thunks.setOrganizationDSOParent({
          OrganizationID: organizationID,
          DSOParentOrganizationID: newParentID ?? '',
        }),
      ).unwrap();

      if (updatedOrganization?.ID) {
        dispatch(
          organizationModel.actions.setNewestOrganization(updatedOrganization),
        );
      }

      notification.success({
        message: formatMessage(parentDSOMessages.updateSuccess),
      });

      onEditorVisibleChange(false);
    } catch (error) {
      notification.error({
        message:
          error instanceof ConnectError
            ? error.message
            : formatMessage({
                id: 'global.error.somethingWentWrong',
                defaultMessage: 'Something went wrong',
              }),
      });
    } finally {
      setLoading(false);
    }
  };

  const parentLinkText = parentName || parentEmail || parentID;
  const parentSecondaryText = parentName && parentEmail;

  return (
    <>
      <span className={styles.label}>
        {formatMessage(organizationsMessages.parentDSOLabel)}
      </span>
      <div className={styles.value}>
        {isEditorVisible ? (
          <div className={styles.formContainer}>
            <OrganizationSearchSelect
              className={styles.select}
              filter={DSOS_FILTER}
              placeholder={formatMessage(
                organizationsMessages.searchPlaceholder,
              )}
              selectedOrganization={parentOrganization}
              value={newParentID}
              onChange={setNewParentID}
            />

            <div className={styles.formButtons}>
              <Button onClick={handleCancel} loading={loading}>
                {formatMessage({
                  id: 'global.cancel',
                  defaultMessage: 'Cancel',
                })}
              </Button>

              <Button
                type="primary"
                disabled={(newParentID ?? '') === parentID}
                onClick={handleUpdateParent}
                loading={loading}
              >
                {formatMessage({ id: 'global.save', defaultMessage: 'Save' })}
              </Button>
            </div>
          </div>
        ) : (
          <span className={styles.editableValue}>
            <Link
              className={styles.parentLink}
              to={generatePath(PATHS.organization, {
                organizationID: parentID,
              })}
            >
              {parentLinkText}
            </Link>

            {!!parentSecondaryText && (
              <Text className={styles.parentEmail}>
                ({parentSecondaryText})
              </Text>
            )}

            {canManageParent && (
              <EditOutlined
                className={styles.editIcon}
                onClick={showEditor}
                rev={undefined}
              />
            )}
          </span>
        )}
      </div>
    </>
  );
};
